import { useIntl } from 'react-intl';

import {
  ACCURACY_JOB_STATUS,
  ACCURACY_JOB_TERMINAL_STATUSES,
} from '../../generate-accuracy/constants';
import type { AccuracyJobSummaryRow } from '../../generate-accuracy/types/response';
import {
  formatAccuracyStageLabel,
  formatEtaSeconds,
  formatTimeAgo,
} from '../emitters/formatAccuracyJobCell';

type JobStatus = 'no-run' | 'pending' | 'in-progress' | 'completed' | 'failed';

interface UseAccuracyJobCellReturn {
  status: JobStatus;
  label: string;
  tooltip: string;
  percent?: number;
  etaSeconds?: number | null;
  secondaryParts?: string[];
  errorMessage?: string;
  isResultsAvailable?: boolean;
}

export const useAccuracyJobCell = (
  job: AccuracyJobSummaryRow | undefined
): UseAccuracyJobCellReturn => {
  const { formatMessage } = useIntl();
  const viewRunHistoryTooltip = formatMessage({
    id: 'adminProjects.accuracyJobCell.viewRunHistoryTooltip',
  });

  if (!job) {
    return {
      status: 'no-run',
      label: formatMessage({ id: 'adminProjects.accuracyJobCell.noRuns' }),
      tooltip: '',
    };
  }

  if (job.status === ACCURACY_JOB_STATUS.PENDING) {
    return {
      status: 'pending',
      label: formatMessage({ id: 'adminProjects.accuracyJobCell.queued' }),
      tooltip: viewRunHistoryTooltip,
    };
  }

  if (!ACCURACY_JOB_TERMINAL_STATUSES.has(job.status)) {
    const percent = job.progress?.percent ?? 0;
    const etaSeconds = job.progress?.etaSeconds ?? null;
    const queuedCount = job.queuedCount ?? 0;
    const stageLabel = formatAccuracyStageLabel(
      job.status,
      job.progress?.currentStep,
      formatMessage({ id: 'adminProjects.accuracyJobCell.inProgress' })
    );
    const label =
      queuedCount > 0
        ? `${stageLabel} · ${formatMessage(
            { id: 'adminProjects.accuracyJobCell.queuedCount' },
            { count: queuedCount }
          )}`
        : stageLabel;
    return {
      status: 'in-progress',
      label,
      tooltip: viewRunHistoryTooltip,
      percent,
      etaSeconds,
    };
  }

  if (job.status === ACCURACY_JOB_STATUS.COMPLETED) {
    const timeAgo = job.finishedAt ? formatTimeAgo(job.finishedAt, formatMessage) : null;
    const isResultsAvailable = job.accuracyIterationId != null;
    const secondaryParts = [timeAgo];

    if (isResultsAvailable) {
      if (job.runCount > 1) {
        secondaryParts.push(
          formatMessage({ id: 'adminProjects.accuracyJobCell.runsCount' }, { count: job.runCount })
        );
      }
    } else {
      secondaryParts.push(
        formatMessage({ id: 'adminProjects.accuracyJobCell.resultsUnavailable' })
      );
    }

    return {
      status: 'completed',
      label: formatMessage({ id: 'adminProjects.accuracyJobCell.completed' }),
      tooltip: viewRunHistoryTooltip,
      secondaryParts: secondaryParts.filter((part): part is string => Boolean(part)),
      isResultsAvailable,
    };
  }

  if (job.status === ACCURACY_JOB_STATUS.FAILED) {
    const errorMessage =
      job.errorMessage || formatMessage({ id: 'adminProjects.accuracyJobCell.failedGeneric' });
    return {
      status: 'failed',
      label: formatMessage({ id: 'adminProjects.accuracyJobCell.failed' }),
      tooltip: errorMessage,
      errorMessage,
    };
  }

  return {
    status: 'no-run',
    label: formatMessage({ id: 'adminProjects.accuracyJobCell.noRuns' }),
    tooltip: '',
  };
};
